/**
 * Chat Service for user-modern — same API as user-classic (communication-service).
 */
import * as authService from './authService'
import { resolveUserChatTenantId } from '../lib/runtimeTenant'

export interface Conversation {
  id?: string
  conversationId?: string
  userLineId?: string
  participants?: {
    user?: { lineUserId?: string; userId?: string; name?: string }
    admin?: { adminId?: string; name?: string }
  }
  status: 'active' | 'closed' | 'archived'
  lastMessage?: {
    content?: string
    timestamp?: string | Date
    senderType?: 'user' | 'admin'
  }
  unreadCount?: number | { user?: number; admin?: number }
  createdAt?: string | Date
  updatedAt?: string | Date
}

export interface MessageAttachment {
  type: string
  url: string
  filename?: string
  size?: number
  thumbnail?: string
  mimeType?: string
}

export interface MessageReplyTo {
  id?: string
  messageId?: string
  content?: string
  sender?: string
  senderName?: string
}

export interface Message {
  id: string
  conversationId: string
  content: string
  contentType?: 'text' | 'image' | 'file' | 'sticker'
  attachments?: MessageAttachment[]
  replyTo?: MessageReplyTo
  senderType: 'user' | 'admin' | 'system'
  timestamp: string | Date
  read?: boolean
}

const getBaseURL = () =>
  (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8081'

class ChatService {
  private communicationServiceUrl: string
  private baseServiceUrl: string
  private lastMarkedReadAtByConversation = new Map<string, number>()

  constructor() {
    const base = getBaseURL()
    this.communicationServiceUrl = `${base}/communication-service-api/api/v1/chat`
    this.baseServiceUrl = `${base}/communication-service-api`
  }

  private resolveTenantId(): string | undefined {
    const user = authService.getCurrentUser()
    const tenantId = resolveUserChatTenantId(user?.tenantId)
    return tenantId || undefined
  }

  private getAuthHeaders(): HeadersInit {
    const token = authService.getToken()
    const tenantId = this.resolveTenantId()
    return {
      'Content-Type': 'application/json',
      ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.communicationServiceUrl}${endpoint}`
    const headers = { ...this.getAuthHeaders(), ...(options.headers as Record<string, string>) }
    const response = await fetch(url, { ...options, headers })
    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`)
    }
    return response.json() as Promise<T>
  }

  resolveFileUrl(path: string): string {
    if (path.startsWith('http')) return path
    return `${this.baseServiceUrl}${path}`
  }

  async getOrCreateConversation(userInfo?: { _id?: string; userId?: string; name?: string; mobile?: string; tenantId?: string }): Promise<Conversation> {
    const user = authService.getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    const authUserId =
      user.id ?? (user as { _id?: string })._id ?? (user as { userId?: string }).userId
    const userId = authUserId ?? userInfo?.userId ?? userInfo?._id
    if (!userId) {
      console.error('❌ [ChatService] User ID not found:', { user, userInfo })
      throw new Error('User ID not found')
    }

    try {
      const response = await this.makeRequest<{ success: boolean; conversations: Conversation[] }>(
        `/users/${userId}/conversations?status=active&limit=1`
      )
      if (response.success && response.conversations && response.conversations.length > 0) {
        return response.conversations[0]
      }

      const userName = userInfo?.name ?? (user as { name?: string }).name ?? user.username
      const userPhone = userInfo?.mobile ?? (user as { phone?: string }).phone ?? (user as { mobile?: string }).mobile ?? user.mobile
      const createResponse = await this.makeRequest<{ success: boolean; conversation: Conversation }>(
        '/conversations',
        {
          method: 'POST',
          body: JSON.stringify({
            userLineId: userId,
            userDetails: {
              name: userName,
              username: user.username,
              phone: userPhone,
              userId,
              tenantId: user.tenantId || userInfo?.tenantId,
            },
            source: 'web_chat',
          }),
        }
      )
      if (createResponse.success && createResponse.conversation) return createResponse.conversation
      throw new Error('Failed to create conversation')
    } catch (error: unknown) {
      const err = error as { message?: string }
      if (err.message?.includes('404') || err.message?.includes('not found')) {
        try {
          const createResponse = await this.makeRequest<{ success: boolean; conversation: Conversation }>(
            '/conversations',
            {
              method: 'POST',
              body: JSON.stringify({
                userLineId: userId,
                userDetails: {
                  name: (user as { name?: string }).name || user.username,
                  username: user.username,
                  phone: (user as { phone?: string }).phone ?? user.mobile,
                },
                source: 'web_chat',
              }),
            }
          )
          if (createResponse.success && createResponse.conversation) return createResponse.conversation
        } catch (createError) {
          console.error('❌ [ChatService] Failed to create conversation:', createError)
        }
      }
      throw error
    }
  }

  private normalizeMessage(m: Record<string, unknown>, fallbackConversationId?: string): Message {
    const contentRaw = m.content as Record<string, unknown> | string | undefined
    let text = ''
    let contentType: Message['contentType'] = 'text'
    let attachments: MessageAttachment[] | undefined

    if (typeof contentRaw === 'object' && contentRaw) {
      text = ((contentRaw as { text?: string }).text ?? '') as string
      const ct = (contentRaw as { type?: string }).type
      if (ct === 'image' || ct === 'file' || ct === 'sticker') contentType = ct
      const rawAtt = (contentRaw as { attachments?: unknown[] }).attachments
      if (Array.isArray(rawAtt) && rawAtt.length > 0) {
        attachments = rawAtt as MessageAttachment[]
      }
    } else {
      text = String(contentRaw ?? '')
    }

    if (!attachments && Array.isArray(m.attachments) && (m.attachments as unknown[]).length > 0) {
      attachments = m.attachments as unknown as MessageAttachment[]
    }

    let replyTo: MessageReplyTo | undefined
    if (m.replyTo && typeof m.replyTo === 'object') {
      const r = m.replyTo as Record<string, unknown>
      replyTo = {
        id: (r.id ?? r.messageId) as string | undefined,
        content: (typeof r.content === 'string' ? r.content : (r.content as { text?: string })?.text) as string | undefined,
        senderName: (r.senderName ?? r.sender) as string | undefined,
      }
    }

    return {
      id: (m.messageId ?? m.id ?? m._id) as string,
      conversationId: (m.conversationId ?? fallbackConversationId ?? '') as string,
      content: text,
      contentType,
      attachments,
      replyTo,
      senderType: ((m.sender as { type?: string })?.type ?? m.senderType ?? 'user') as Message['senderType'],
      timestamp: (m.createdAt ?? m.timestamp ?? new Date().toISOString()) as string,
      read: m.read as boolean | undefined,
    }
  }

  async getMessages(conversationId: string, limit = 50, since?: string): Promise<Message[]> {
    try {
      const sinceQuery = since ? `&since=${encodeURIComponent(since)}` : ''
      const response = await this.makeRequest<{ success: boolean; messages: Message[] }>(
        `/conversations/${conversationId}/messages?limit=${limit}${sinceQuery}`
      )
      if (response.success && response.messages) {
        return (response.messages as unknown as Record<string, unknown>[])
          .map((m) => this.normalizeMessage(m, conversationId))
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      }
      return []
    } catch (error) {
      console.error('❌ [ChatService] Failed to get messages:', error)
      return []
    }
  }

  async sendMessage(
    conversationId: string,
    content: string,
    options?: {
      contentType?: string
      attachments?: MessageAttachment[]
      replyTo?: { id?: string; content?: string; senderName?: string }
    },
  ): Promise<Message> {
    const user = authService.getCurrentUser()
    const senderId = (user as { userId?: string })?.userId ?? (user as { _id?: string })?._id ?? user?.id
    if (!senderId) throw new Error('User not authenticated')

    const body: Record<string, unknown> = {
      senderId: String(senderId),
      senderType: 'user',
      content: {
        text: content,
        type: options?.contentType || 'text',
        ...(options?.attachments?.length ? { attachments: options.attachments } : {}),
      },
    }
    if (options?.attachments?.length) body.attachments = options.attachments
    if (options?.replyTo) body.replyTo = options.replyTo

    const response = await this.makeRequest<{ success: boolean; message: Message }>(
      `/conversations/${conversationId}/messages`,
      { method: 'POST', body: JSON.stringify(body) },
    )
    if (response.success && response.message) {
      return this.normalizeMessage(response.message as unknown as Record<string, unknown>, conversationId)
    }
    throw new Error('Failed to send message')
  }

  async uploadFile(file: File): Promise<{ success: boolean; url: string; filename: string; size: number; type: string }> {
    const formData = new FormData()
    formData.append('file', file)
    const token = authService.getToken()
    const tenantId = this.resolveTenantId()

    const response = await fetch(`${this.communicationServiceUrl}/upload`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      },
      body: formData,
    })
    if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`)
    return response.json()
  }

  async deleteMessage(conversationId: string, messageId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/conversations/${conversationId}/messages/${messageId}`, {
      method: 'DELETE',
    })
  }

  async markAsRead(conversationId: string, userId?: string): Promise<void> {
    const now = Date.now()
    const lastMarkedReadAt = this.lastMarkedReadAtByConversation.get(conversationId) ?? 0
    if (now - lastMarkedReadAt < 5000) {
      return
    }

    const user = authService.getCurrentUser()
    const readerId = userId ?? user?.id ?? (user as { _id?: string })?._id ?? (user as { userId?: string })?.userId
    if (!readerId) {
      console.warn('⚠️ [ChatService] No readerId available for markAsRead')
      return
    }
    try {
      this.lastMarkedReadAtByConversation.set(conversationId, now)
      await this.makeRequest(`/conversations/${conversationId}/read`, {
        method: 'PUT',
        body: JSON.stringify({ readerType: 'user', readerId }),
      })
    } catch (error) {
      this.lastMarkedReadAtByConversation.delete(conversationId)
      console.error('❌ [ChatService] Failed to mark as read:', error)
    }
  }
}

export const chatService = new ChatService()
